import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";
import cx from "classnames";
import React, { useEffect } from "react";

import { Btn, BtnSidePadding, BtnType } from "@/mk2/components/Btn";
import { Dot } from "@/mk2/components/Dot";
import { LoadingPlaceholder } from "@/mk2/components/LoadingPlaceholder";
import {
    BZ2_ITEM_DETAIL_REPORT_BUTTON,
    BZ2_ITEM_DETAIL_REPORT_MARK_OK,
    BZ2_ITEM_DETAIL_REPORT_MARK_OK_AND_NEXT,
    BZ2_PRODUCT_HAS_COMPLAINTS,
    PRODUCT_REPORT_REPORT_PROBLEM,
} from "@/mk/autogenerated/translation";

import styles from "./ProductReport.mscss";

interface OwnProps {
    className?: string;
    isActive?: boolean;
    isDeleted?: boolean;
    isReported?: boolean;
    isModerator?: boolean;
    hasNavigation?: boolean;
    isMarkingOK?: boolean;
    isMarkedOK?: boolean;

    onReport();
    onMarkOk();
    onMarkOkAndNext();
}

type Props = OwnProps;

const ProductReport: React.FunctionComponent<Props> = ({
    className,
    isActive = true,
    isDeleted = false,
    isReported = false,
    isModerator = false,
    hasNavigation = false,
    isMarkingOK = false,
    isMarkedOK = false,
    onReport,
    onMarkOk,
    onMarkOkAndNext,
}) => {
    // Key handler
    useEffect(() => {
        const handler = (event: KeyboardEvent) => {
            // Ignore inputs
            if (
                ["input", "textarea", "select"].indexOf(
                    (event.target as HTMLElement).tagName.toLowerCase(),
                ) !== -1
            ) {
                return;
            }

            if (event.metaKey || event.altKey || event.ctrlKey) {
                return;
            }

            if (event.keyCode === 79) {
                // O as OK
                onMarkOk();
            } else if (event.keyCode === 78) {
                // N as next
                if (hasNavigation) {
                    onMarkOkAndNext();
                }
            }
        };

        if (isModerator && !isMarkedOK && !isMarkingOK && !isDeleted) {
            window.addEventListener("keyup", handler);
        }

        return () => {
            window.removeEventListener("keyup", handler);
        };
    }, [isModerator, isMarkedOK, isMarkingOK, onMarkOk, hasNavigation, isMarkingOK, isDeleted]);

    return (
        <div className={cx(styles.ProductReport, className)}>
            {isModerator && !isMarkedOK && !isDeleted && (
                <>
                    <Btn
                        type={BtnType.ModeratorTextLight}
                        label={BZ2_ITEM_DETAIL_REPORT_MARK_OK}
                        sidePadding={BtnSidePadding.None}
                        onClick={onMarkOk}
                        hasSpinner={isMarkingOK}
                    />
                    <Dot spaces={1} />
                    {hasNavigation && !isMarkingOK && (
                        <>
                            <Btn
                                type={BtnType.ModeratorTextLight}
                                label={BZ2_ITEM_DETAIL_REPORT_MARK_OK_AND_NEXT}
                                sidePadding={BtnSidePadding.None}
                                onClick={onMarkOkAndNext}
                            />
                            <Dot spaces={1} />
                        </>
                    )}
                </>
            )}
            {isReported ? (
                BZ2_PRODUCT_HAS_COMPLAINTS
            ) : isActive ? (
                <Btn
                    type={BtnType.BlueTextLight}
                    icon={faExclamationTriangle}
                    label={BZ2_ITEM_DETAIL_REPORT_BUTTON}
                    sidePadding={BtnSidePadding.None}
                    onClick={onReport}
                />
            ) : (
                <Btn
                    type={BtnType.BlueTextLight}
                    icon={faExclamationTriangle}
                    label={PRODUCT_REPORT_REPORT_PROBLEM}
                    link="/help/#cat-buysell-problem"
                    sidePadding={BtnSidePadding.None}
                />
            )}
        </div>
    );
};

ProductReport.displayName = "ProductReport";

export default React.memo<Props>(ProductReport);

interface PlaceholderOwnProps {
    className?: string;
}

export const ProductReportPlaceholder = React.memo<PlaceholderOwnProps>(({ className }) => {
    return (
        <div className={cx(styles.ProductReport, className)}>
            <div style={{ lineHeight: "34px" /* simulate btn height */ }}>
                <LoadingPlaceholder width={148} />
            </div>
        </div>
    );
});

ProductReportPlaceholder.displayName = "ProductReportPlaceholder";
